import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Building2,
  UtensilsCrossed,
  Car,
  Ticket,
  Droplets,
  Video,
  Clock,
  MapPin,
  Phone,
  Train,
  ChevronDown,
  Shield,
  Landmark,
  ArrowRight,
  ExternalLink,
  HelpCircle,
  UserCircle,
} from 'lucide-react'
import { useDhamLang } from '../context/ManaunaDhamLangContext'
import WeatherWidget from './manauna-dham/WeatherWidget'
import SocialSection from './manauna-dham/SocialSection'
import LangToggle from './manauna-dham/LangToggle'

const asset = (name: string) => `${import.meta.env.BASE_URL}manauna-dham/${name}`

function ManaunaDhamInner() {
  const { t } = useDhamLang()
  const [openFaq, setOpenFaq] = useState<number | null>(0)

  const stats = [
    {
      value: '3L+',
      label: t({ en: 'Pilgrim journeys planned', hi: 'तीर्थयात्रियों की यात्राएं', hinglish: 'Pilgrim journeys planned' }),
      sub: t({ en: 'Collective reach across seasons', hi: 'मौसमों भर में सामूहिक पहुंच', hinglish: 'Har mausam mein reach' }),
    },
    {
      value: '15+',
      label: t({ en: 'Concierge-style services', hi: 'सेवाएं उपलब्ध', hinglish: 'Services available' }),
      sub: t({ en: 'Stays, meals, transport, and more', hi: 'ठहरना, खाना, यातायात और बहुत कुछ', hinglish: 'Stay, khana, transport aur bahut kuch' }),
    },
    {
      value: '24/7',
      label: t({ en: 'Assistance mindset', hi: 'सहायता की भावना', hinglish: 'Help ka mindset' }),
      sub: t({ en: 'We respond as fast as we can', hi: 'हम जितना जल्दी हो सके जवाब देते हैं', hinglish: 'Jitna jaldi ho sake reply karte hain' }),
    },
    {
      value: '1500+',
      label: t({ en: 'Guests supported', hi: 'मेहमानों की मदद', hinglish: 'Guests ki madad' }),
      sub: t({ en: 'Through Aonla Stay and partners', hi: 'Aonla Stay और साझेदारों के माध्यम से', hinglish: 'Aonla Stay aur partners ke through' }),
    },
  ]

  const services = [
    {
      title: t({ en: 'Hotel / Dharmshala', hi: 'होटल / धर्मशाला', hinglish: 'Hotel / Dharmshala' }),
      desc: t({
        en: 'Curated stay options near the Dham with clear expectations and host-backed listings.',
        hi: 'धाम के पास ठहरने के विकल्प, स्पष्ट जानकारी के साथ।',
        hinglish: 'Dham ke paas rukhne ke options, sab clear info ke saath.',
      }),
      icon: Building2,
      image: asset('ILoveManaunaDham.webp'),
      link: '/manauna-dham/accommodations',
    },
    {
      title: t({ en: 'Food coordination', hi: 'खाने की व्यवस्था', hinglish: 'Khana ka intezaam' }),
      desc: t({
        en: 'Home-style meals and local kitchen partners for devotees who prefer delivery or dine-in help.',
        hi: 'भक्तों के लिए घर जैसा खाना और स्थानीय रसोई साझेदार।',
        hinglish: 'Bhakton ke liye ghar jaisa khana aur local kitchen partners.',
      }),
      icon: UtensilsCrossed,
      image: asset('Gathering.webp'),
      link: '/manauna-dham/food',
    },
    {
      title: t({ en: 'Transport', hi: 'परिवहन', hinglish: 'Transport' }),
      desc: t({
        en: 'Taxi, auto, and route guidance so you reach Manauna village comfortably from Bareilly and beyond.',
        hi: 'टैक्सी, ऑटो और मार्गदर्शन ताकि आप बरेली और आसपास से आसानी से पहुंचें।',
        hinglish: 'Taxi, auto aur route guidance taaki aap aasani se pahunchein.',
      }),
      icon: Car,
      image: asset('EntryGate.webp'),
      link: '/manauna-dham/transport',
    },
    {
      title: t({ en: 'Patient token guidance', hi: 'पेशेंट टोकन मार्गदर्शन', hinglish: 'Patient Token guidance' }),
      desc: t({
        en: 'Practical, on-ground oriented information. Tokens follow temple practices; we do not issue them remotely.',
        hi: 'व्यावहारिक जानकारी। टोकन मंदिर की प्रथाओं का पालन करते हैं।',
        hinglish: 'Practical jaankari. Tokens mandir ke rules follow karte hain.',
      }),
      icon: Ticket,
      image: asset('Patient1.webp'),
      link: '/manauna-dham/patient-token',
    },
    {
      title: t({ en: 'Shyam Jal', hi: 'श्याम जल', hinglish: 'Shyam Jal' }),
      desc: t({
        en: 'Information on sacred water services as commonly arranged for pilgrims visiting the region.',
        hi: 'तीर्थयात्रियों के लिए पवित्र जल सेवाओं की जानकारी।',
        hinglish: 'Tirth yatriyon ke liye pavitra jal ki jaankari.',
      }),
      icon: Droplets,
      image: asset('KhatuShyam.webp'),
      link: '/manauna-dham/shyam-jal',
    },
    {
      title: t({ en: 'Live darshan', hi: 'लाइव दर्शन', hinglish: 'Live Darshan' }),
      desc: t({
        en: 'Pointers to official or community streams when available; schedules may change without notice.',
        hi: 'जब उपलब्ध हो तब आधिकारिक या सामुदायिक स्ट्रीम्स के संकेत।',
        hinglish: 'Jab available ho tab official ya community streams ke pointers.',
      }),
      icon: Video,
      image: asset('Omendra.webp'),
      link: '/manauna-dham/live-darshan',
    },
  ]

  const quickLinks = [
    { to: '/manauna-dham/khatu-shyam', label: t({ en: 'About Shri Khatu Shyam Ji', hi: 'श्री खाटू श्याम जी के बारे में', hinglish: 'Shri Khatu Shyam Ji ke baare mein' }), icon: Landmark },
    { to: '/manauna-dham/mahant', label: t({ en: 'About Mahant Omendra Ji', hi: 'महंत ओमेंद्र जी के बारे में', hinglish: 'Mahant Omendra Ji ke baare mein' }), icon: UserCircle },
    { to: '/manauna-dham/contact', label: t({ en: 'Complete Guide & Contact', hi: 'पूरी जानकारी और संपर्क', hinglish: 'Complete Guide & Contact' }), icon: Phone },
  ]

  const gallery = [
    { src: asset('EntryGate.webp'), alt: 'Entry gate of Manauna Mandir' },
    { src: asset('Gathering.webp'), alt: 'Gathering at Manauna Dham' },
    { src: asset('Gathering2.webp'), alt: 'Devotees at the Dham' },
    { src: asset('night.webp'), alt: 'Khatu Shyam Mandir at night' },
    { src: asset('KhatuShyam.webp'), alt: 'Shri Khatu Shyam Ji' },
    { src: asset('Mandir5.webp'), alt: 'Manauna Mandir Khatu Shyam Ji' },
    { src: asset('Mandir1.webp'), alt: 'Mandir precinct' },
    { src: asset('Mandir6.webp'), alt: 'Shyam Ji Mandir' },
    { src: asset('Omendra.webp'), alt: 'Mahant Ji' },
    { src: asset('Omendra3.webp'), alt: 'Mahant Omendra Ji' },
    { src: asset('ILoveManaunaDham.webp'), alt: 'Manauna Dham' },
    { src: asset('Patient1.webp'), alt: 'Devotees' },
  ]

  const faqs = [
    {
      q: t({ en: 'Where is Manauna Dham located?', hi: 'मनौना धाम कहाँ है?', hinglish: 'Manauna Dham kahan hai?' }),
      a: t({
        en: 'Manauna Dham is in Manauna village, Tehsil Aonla, Bareilly district, Uttar Pradesh – 243301. It is widely visited by devotees of Shri Khatu Shyam Ji.',
        hi: 'मनौना धाम, मनौना गांव, तहसील अनोला, बरेली जिला, उत्तर प्रदेश – 243301 में है। यह श्री खाटू श्याम जी के भक्तों द्वारा व्यापक रूप से दर्शन किया जाता है।',
        hinglish: 'Manauna Dham, Manauna village, Tehsil Aonla, Bareilly district, UP – 243301 mein hai. Shri Khatu Shyam Ji ke bhakton dwara widely visit hota hai.',
      }),
    },
    {
      q: t({ en: 'What are darshan and aarti timings?', hi: 'दर्शन और आरती का समय क्या है?', hinglish: 'Darshan aur aarti ka time kya hai?' }),
      a: t({
        en: 'Typical darshan window is 7 AM – 9 PM with aarti around 8 AM and 5 PM. Ground schedules can change; please confirm at the temple when you arrive.',
        hi: 'आमतौर पर दर्शन 7 AM – 9 PM तक होते हैं, आरती लगभग 8 AM और 5 PM। समय बदल सकता है, कृपया मंदिर पर पुष्टि करें।',
        hinglish: 'Darshan 7 AM – 9 PM tak hote hain, aarti 8 AM aur 5 PM. Time change ho sakta hai, mandir par confirm kar lein.',
      }),
    },
    {
      q: t({ en: 'How do patient tokens work?', hi: 'पेशेंट टोकन कैसे काम करते हैं?', hinglish: 'Patient tokens kaise kaam karte hain?' }),
      a: t({
        en: 'Patient tokens are tied to on-site presence and temple practice. We share orientation only; we do not issue tokens online.',
        hi: 'पेशेंट टोकन उपस्थिति और मंदिर की प्रथा से जुड़े हैं। हम केवल जानकारी साझा करते हैं; हम ऑनलाइन टोकन जारी नहीं करते।',
        hinglish: 'Patient tokens on-site presence aur mandir ki practice se jude hain. Hum sirf jaankari share karte hain; online tokens issue nahi karte.',
      }),
    },
    {
      q: t({ en: 'Is Aonla Stay the official temple website?', hi: 'क्या Aonla Stay आधिकारिक मंदिर वेबसाइट है?', hinglish: 'Kya Aonla Stay official temple website hai?' }),
      a: t({
        en: 'No. Aonla Stay is an independent hospitality and bookings platform. For official pronouncements, rely on temple management and on-ground staff.',
        hi: 'नहीं। Aonla Stay एक स्वतंत्र आतिथ्य और बुकिंग प्लेटफॉर्म है। आधिकारिक घोषणाओं के लिए मंदिर प्रशासन पर निर्भर रहें।',
        hinglish: 'Nahi. Aonla Stay ek independent hospitality aur booking platform hai. Official info ke liye mandir management se confirm karein.',
      }),
    },
  ]

  return (
    <div className="bg-secondary-50 text-secondary-900">
      {/* Hero */}
      <section className="relative min-h-[78vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url(${asset('night.webp')})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-secondary-900 via-secondary-900/75 to-secondary-900/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-32 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-3 rounded-full border border-primary-400/40 bg-secondary-900/40 backdrop-blur-md pl-2 pr-4 py-2 text-primary-200 text-sm font-medium mb-6">
              <img
                src={asset('logonobg.webp')}
                alt="Manauna Dham logo"
                width={40}
                height={40}
                className="h-10 w-10 object-contain rounded-lg bg-white/10 p-1"
              />
              <span className="flex items-center gap-2">
                <Landmark className="h-4 w-4 text-primary-400 shrink-0" />
                {t({
                  en: 'Manauna (Manona) Dham · Bareilly region',
                  hi: 'मनौना (मनोना) धाम · बरेली क्षेत्र',
                  hinglish: 'Manauna (Manona) Dham · Bareilly region',
                })}
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
              {t({ en: 'Sacred journeys,', hi: 'पवित्र यात्राएं,', hinglish: 'Pavitra yaatraein,' })}
              <span className="block text-primary-300 mt-1">
                {t({ en: 'thoughtful stays', hi: 'सोच-समझ कर ठहराव', hinglish: 'soch-samajh ke theraav' })}
              </span>
            </h1>
            <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-2xl">
              {t({
                en: 'A premium, readable guide for pilgrims—paired with Aonla Stay room bookings, local coordination, and clear independence from temple administration.',
                hi: 'तीर्थयात्रियों के लिए एक प्रीमियम गाइड—Aonla Stay रूम बुकिंग, स्थानीय समन्वय, और मंदिर प्रशासन से स्पष्ट स्वतंत्रता के साथ।',
                hinglish: 'Tirth yatriyon ke liye ek premium guide—Aonla Stay room booking, local coordination, aur temple administration se clear independence ke saath.',
              })}
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/rooms"
                className="inline-flex items-center gap-2 rounded-xl bg-primary-500 px-6 py-3.5 text-white font-semibold shadow-lg shadow-primary-900/30 hover:bg-primary-600 transition-colors"
              >
                {t({ en: 'Book a stay', hi: 'बुक करें', hinglish: 'Book karein' })}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-xl border-2 border-white/35 bg-white/10 backdrop-blur-sm px-6 py-3.5 text-white font-semibold hover:bg-white/15 transition-colors"
              >
                <Phone className="h-4 w-4" />
                {t({ en: 'Talk to us', hi: 'बात करें', hinglish: 'Baat karein' })}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-secondary-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
            {stats.map((s) => (
              <div key={s.label} className="relative pl-4 border-l-2 border-primary-400">
                <div className="text-3xl sm:text-4xl font-bold text-secondary-900">{s.value}</div>
                <div className="mt-1 font-semibold text-secondary-800">{s.label}</div>
                <p className="mt-2 text-sm text-secondary-600 leading-snug">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Weather Widget */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <WeatherWidget />
        </div>
      </section>

      {/* Services */}
      <section className="pb-20 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900">
              {t({ en: 'Services we help coordinate', hi: 'सेवाएं जिनमें हम मदद करते हैं', hinglish: 'Services jinmein hum madad karte hain' })}
            </h2>
            <p className="mt-4 text-secondary-600 text-lg leading-relaxed">
              {t({
                en: 'Inspired by the breadth of pilgrim needs around Manauna Dham—presented in a calm, premium layout that matches Aonla Stay.',
                hi: 'मनौना धाम के आसपास तीर्थयात्रियों की ज़रूरतों से प्रेरित—Aonla Stay के अनुरूप शांत, प्रीमियम लेआउट में।',
                hinglish: 'Manauna Dham ke aaspaas bhakton ki zarooraton se inspired—Aonla Stay ke premium layout mein.',
              })}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((item) => {
              const Icon = item.icon
              return (
                <article
                  key={item.link}
                  className="group rounded-2xl bg-white border border-secondary-200/80 shadow-sm hover:shadow-xl hover:border-primary-200/80 transition-all duration-300 overflow-hidden flex flex-col"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary-900/70 to-transparent" />
                    <div className="absolute bottom-4 left-4 w-11 h-11 rounded-xl bg-primary-500 flex items-center justify-center shadow-lg">
                      <Icon className="h-5 w-5 text-white" strokeWidth={2} />
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-xl font-bold text-secondary-900">{item.title}</h3>
                    <p className="mt-3 text-secondary-600 text-sm leading-relaxed flex-1">{item.desc}</p>
                    <Link
                      to={item.link}
                      className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700"
                    >
                      {t({ en: 'Learn more', hi: 'और जानें', hinglish: 'Aur jaanein' })}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              )
            })}
          </div>

          {/* Quick links to about pages */}
          <div className="mt-12 grid sm:grid-cols-3 gap-4">
            {quickLinks.map((ql) => {
              const Icon = ql.icon
              return (
                <Link
                  key={ql.to}
                  to={ql.to}
                  className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-secondary-200 shadow-sm hover:shadow-lg hover:border-primary-200 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center shrink-0 group-hover:bg-primary-200 transition-colors">
                    <Icon className="h-6 w-6 text-primary-700" />
                  </div>
                  <div>
                    <div className="font-semibold text-secondary-900 group-hover:text-primary-700 transition-colors">
                      {ql.label}
                    </div>
                    <div className="text-xs text-secondary-500 mt-0.5 flex items-center gap-1">
                      {t({ en: 'Read more', hi: 'और पढ़ें', hinglish: 'Aur padhein' })}
                      <ArrowRight className="h-3 w-3" />
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* About + timings */}
      <section className="py-20 bg-white border-y border-secondary-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900">
                {t({ en: 'Manauna Dham', hi: 'मनौना धाम', hinglish: 'Manauna Dham' })}
              </h2>
              <p className="mt-2 text-sm font-semibold uppercase tracking-wider text-primary-600">
                {t({
                  en: 'Also referred to as Manona · near Aonla',
                  hi: 'मनोना के नाम से भी जाना जाता है · अनोला के पास',
                  hinglish: 'Manona ke naam se bhi jaana jaata hai · Aonla ke paas',
                })}
              </p>
              <p className="mt-6 text-secondary-600 leading-relaxed">
                {t({
                  en: 'Shri Khatu Shyam Ji Manauna Dham is a growing pilgrimage destination in Bareilly district. Devotees arrive for darshan, aarti, and family vows. The correct spelling is Manauna; Manona is a common spoken variant you will still hear locally and online.',
                  hi: 'श्री खाटू श्याम जी मनौना धाम बरेली जिले में एक बढ़ता हुआ तीर्थ स्थल है। भक्त दर्शन, आरती और पारिवारिक मन्नतों के लिए आते हैं।',
                  hinglish: 'Shri Khatu Shyam Ji Manauna Dham Bareilly district mein ek badhta hua tirth sthal hai. Bhakt darshan, aarti aur family vows ke liye aate hain. Sahi spelling Manauna hai; Manona bola jaata hai locally.',
                })}
              </p>
              <p className="mt-4 text-secondary-600 leading-relaxed">
                {t({
                  en: 'Mahant Ji and temple rituals anchor the experience. Respectful dress, patience in queues, and confirming seasonal rush hours on site will make your visit smoother.',
                  hi: 'महंत जी और मंदिर की रस्में अनुभव को जोड़ती हैं। सम्मानजनक पोशाक, कतार में धैर्य, और मौसमी भीड़ की पुष्टि करने से आपकी यात्रा आसान होगी।',
                  hinglish: 'Mahant Ji aur mandir ki rasmein experience ko anchor karti hain. Respectful dress, queue mein patience aur seasonal rush ki info se visit smooth hogi.',
                })}
              </p>
              <div className="mt-8 rounded-2xl bg-secondary-50 border border-secondary-200 p-6">
                <div className="flex items-start gap-3">
                  <Clock className="h-6 w-6 text-primary-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-secondary-900">
                      {t({ en: 'Mandir timings (indicative)', hi: 'मंदिर का समय (संकेतात्मक)', hinglish: 'Mandir ka time (indicative)' })}
                    </h3>
                    <ul className="mt-3 space-y-2 text-secondary-600 text-sm">
                      <li className="flex justify-between gap-4 border-b border-secondary-200/80 pb-2">
                        <span>{t({ en: 'Darshan', hi: 'दर्शन', hinglish: 'Darshan' })}</span>
                        <span className="font-medium text-secondary-800">7:00 – 21:00</span>
                      </li>
                      <li className="flex justify-between gap-4 border-b border-secondary-200/80 pb-2">
                        <span>{t({ en: 'Aarti', hi: 'आरती', hinglish: 'Aarti' })}</span>
                        <span className="font-medium text-secondary-800">8:00 &amp; 17:00</span>
                      </li>
                      <li className="flex justify-between gap-4 border-b border-secondary-200/80 pb-2">
                        <span>{t({ en: 'Favourable season', hi: 'अनुकूल मौसम', hinglish: 'Best season' })}</span>
                        <span className="font-medium text-secondary-800">
                          {t({ en: 'June – December', hi: 'जून – दिसंबर', hinglish: 'June – December' })}
                        </span>
                      </li>
                      <li className="flex justify-between gap-4 pt-1">
                        <span>{t({ en: 'Mahant Ji availability', hi: 'महंत जी की उपलब्धता', hinglish: 'Mahant Ji ki availability' })}</span>
                        <span className="font-medium text-secondary-800 text-right">
                          {t({
                            en: 'Often Wed & Thu away — verify locally',
                            hi: 'अक्सर बुध और गुरु को नहीं — स्थानीय पुष्टि करें',
                            hinglish: 'Aksar Wed & Thu nahi — locally verify karein',
                          })}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-primary-200/40 to-transparent rounded-3xl blur-2xl" />
              <img
                src={asset('Mandir5.webp')}
                alt="Manauna Mandir Khatu Shyam Ji"
                className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/5] lg:aspect-auto lg:h-[560px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Location & journey */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 rounded-2xl bg-secondary-900 text-white p-8 sm:p-10 shadow-xl">
              <MapPin className="h-8 w-8 text-primary-400 mb-4" />
              <h2 className="text-2xl sm:text-3xl font-bold">
                {t({ en: 'Temple location', hi: 'मंदिर का स्थान', hinglish: 'Mandir ka location' })}
              </h2>
              <p className="mt-4 text-white/80 leading-relaxed max-w-xl">
                {t({
                  en: 'Shri Khatu Shyam Mandir, Manauna Village, Tehsil Aonla, Bareilly, Uttar Pradesh – 243301',
                  hi: 'श्री खाटू श्याम मंदिर, मनौना गांव, तहसील अनोला, बरेली, उत्तर प्रदेश – 243301',
                  hinglish: 'Shri Khatu Shyam Mandir, Manauna Village, Tehsil Aonla, Bareilly, UP – 243301',
                })}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Shri+Khatu+Shyam+Mandir+Manauna+Bareilly"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl bg-primary-500 px-5 py-3 font-semibold hover:bg-primary-600 transition-colors"
                >
                  {t({ en: 'Open in Maps', hi: 'Maps में खोलें', hinglish: 'Maps mein kholein' })}
                  <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  href="tel:9457425914"
                  className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-5 py-3 font-semibold hover:bg-white/10 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  9457425914
                </a>
              </div>
            </div>
            <div className="space-y-6">
              <div className="rounded-2xl bg-white border border-secondary-200 p-6 shadow-sm h-full">
                <Train className="h-7 w-7 text-primary-600 mb-3" />
                <h3 className="font-bold text-secondary-900 text-lg">
                  {t({ en: 'Plan by rail', hi: 'रेल से योजना बनाएं', hinglish: 'Rail se plan banayein' })}
                </h3>
                <p className="mt-2 text-sm text-secondary-600 leading-relaxed">
                  {t({
                    en: 'Bareilly junction is the nearest major rail hub. Pre-book onward road transport during festivals.',
                    hi: 'बरेली जंक्शन निकटतम प्रमुख रेल केंद्र है। त्योहारों के दौरान आगे की सड़क यात्रा पहले बुक करें।',
                    hinglish: 'Bareilly junction nearest major rail hub hai. Festivals ke time road transport pehle book kar lein.',
                  })}
                </p>
                <a
                  href="https://www.irctc.co.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700"
                >
                  IRCTC
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
              <div className="rounded-2xl bg-white border border-secondary-200 p-6 shadow-sm">
                <Car className="h-7 w-7 text-primary-600 mb-3" />
                <h3 className="font-bold text-secondary-900 text-lg">
                  {t({ en: 'Road transfer', hi: 'सड़क स्थानांतरण', hinglish: 'Road transfer' })}
                </h3>
                <p className="mt-2 text-sm text-secondary-600 leading-relaxed">
                  {t({
                    en: 'Taxis and autos can be arranged with notice. Share your train or bus arrival time when you call.',
                    hi: 'पूर्व सूचना पर टैक्सी और ऑटो की व्यवस्था की जा सकती है।',
                    hinglish: 'Taxi aur auto advance mein arrange ho sakte hain. Call karte waqt train ya bus ka time batayein.',
                  })}
                </p>
                <Link
                  to="/manauna-dham/transport"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 hover:text-primary-700"
                >
                  {t({ en: 'View transport options', hi: 'परिवहन विकल्प देखें', hinglish: 'Transport options dekho' })}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
          <div className="mt-12 w-full">
            <div className="rounded-2xl overflow-hidden border border-secondary-200 shadow-lg bg-white">
              <iframe
                title="Manauna Dham Shyam Ji Mandir on Google Maps"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d878.4749208605006!2d79.14032607221374!3d28.2710585!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390a9b413b39cef9%3A0x4dbdb472d874534b!2sManauna%20Dham%20Shyam%20Ji%20Mandir!5e0!3m2!1sen!2sin"
                width="100%"
                height={450}
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Social Section */}
      <SocialSection />

      {/* Gallery */}
      <section className="py-20 bg-white border-t border-secondary-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-secondary-900">
            {t({ en: 'Temple gallery', hi: 'मंदिर गैलरी', hinglish: 'Mandir Gallery' })}
          </h2>
          <p className="mt-3 text-secondary-600 max-w-2xl">
            {t({
              en: 'Moments from around Shri Khatu Shyam Mandir, Manauna—shared to help you plan your visit with Aonla Stay.',
              hi: 'श्री खाटू श्याम मंदिर, मनौना के आसपास के पल—Aonla Stay के साथ आपकी यात्रा की योजना बनाने में मदद के लिए।',
              hinglish: 'Shri Khatu Shyam Mandir, Manauna ke aaspaas ke pal—Aonla Stay ke saath visit plan karne mein madad ke liye.',
            })}
          </p>
          <div className="mt-12 columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
            {gallery.map((g) => (
              <div key={g.src + g.alt} className="break-inside-avoid rounded-xl overflow-hidden border border-secondary-200 shadow-sm">
                <img src={g.src} alt={g.alt} className="w-full object-cover hover:scale-[1.02] transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-primary-700 font-semibold mb-2">
            <HelpCircle className="h-5 w-5" />
            {t({ en: 'Frequently asked', hi: 'अक्सर पूछे जाने वाले', hinglish: 'Aksar poochhe jaane wale' })}
          </div>
          <h2 className="text-3xl font-bold text-secondary-900">
            {t({ en: 'Before you travel', hi: 'यात्रा से पहले', hinglish: 'Yatra se pehle' })}
          </h2>
          <div className="mt-10 space-y-3">
            {faqs.map((f, i) => (
              <div
                key={i}
                className="rounded-xl border border-secondary-200 bg-white overflow-hidden shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 font-semibold text-secondary-900 hover:bg-secondary-50/80 transition-colors"
                >
                  {f.q}
                  <ChevronDown
                    className={`h-5 w-5 text-secondary-500 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-secondary-600 text-sm leading-relaxed border-t border-secondary-100 pt-3">
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disclaimer + CTA */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-secondary-800 to-secondary-900 text-white p-8 sm:p-12 lg:p-14 shadow-2xl">
            <div className="flex flex-col lg:flex-row lg:items-start gap-10">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-200 mb-4">
                  <Shield className="h-3.5 w-3.5" />
                  {t({ en: 'Legal clarity', hi: 'कानूनी स्पष्टता', hinglish: 'Legal clarity' })}
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold">
                  {t({ en: 'Independent platform', hi: 'स्वतंत्र प्लेटफॉर्म', hinglish: 'Independent platform' })}
                </h2>
                <p className="mt-4 text-white/75 leading-relaxed text-sm sm:text-base">
                  {t({
                    en: 'Aonla Stay focuses on room bookings and guest services. Temple timings, rituals, and on-ground rules belong to mandir administration. This guide and gallery are for general orientation only—confirm sensitive arrangements directly at the Dham.',
                    hi: 'Aonla Stay कमरे की बुकिंग और अतिथि सेवाओं पर केंद्रित है। मंदिर का समय, रीति-रिवाज और नियम मंदिर प्रशासन के हैं। यह गाइड और गैलरी केवल सामान्य जानकारी के लिए है।',
                    hinglish: 'Aonla Stay room booking aur guest services par focus karta hai. Temple timings, rituals aur rules mandir administration ke hain. Yeh guide aur gallery sirf general orientation ke liye hai.',
                  })}
                </p>
              </div>
              <div className="lg:w-80 shrink-0 flex flex-col gap-4">
                <Link
                  to="/rooms"
                  className="inline-flex justify-center items-center gap-2 rounded-xl bg-primary-500 px-6 py-4 font-bold text-secondary-900 hover:bg-primary-400 transition-colors"
                >
                  {t({ en: 'Browse rooms', hi: 'कमरे देखें', hinglish: 'Rooms dekho' })}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/"
                  className="inline-flex justify-center items-center gap-2 rounded-xl border border-white/25 px-6 py-4 font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  {t({ en: 'Back to home', hi: 'होम पर वापस', hinglish: 'Home pe wapas' })}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floating language toggle */}
      <div className="fixed bottom-6 right-6 z-50">
        <LangToggle />
      </div>
    </div>
  )
}

export default function ManaunaDham() {
  return <ManaunaDhamInner />
}
